import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Star, MapPin, Languages, Award, MessageSquare, Calendar, Clock, Users, Heart, Shield, CheckCircle, Moon, Music } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingModal from '../components/BookingModal';

interface Experience {
  id: number;
  title: string;
  image: string;
  duration: string;
  groupSize: string;
  price: number;
  rating: number;
  reviews: number;
  location: string;
  description: string;
}

interface Guide {
  id: string;
  name: string;
  image: string;
  location: string;
  languages: string[];
  specialties: string[];
  experience: number;
  rating: number;
  reviews: number;
  bio: string;
  verified: boolean;
  joinedDate: string;
  completedTours: number;
  responseTime: string;
  experiences: Experience[];
  certifications: string[];
  achievements: {
    title: string;
    description: string;
  }[];
}

const GuideProfile = () => {
  const { id } = useParams<{ id: string }>();
  const [guide, setGuide] = useState<Guide | null>(null);
  const [selectedExperience, setSelectedExperience] = useState<Experience | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'experiences' | 'about'>('experiences');

  useEffect(() => {
    // In a real app, fetch guide data from API
    // For now, using mock data
    const mockGuides: Record<string, Guide> = {
      '1': {
        id: '1',
        name: 'Agaba Mathius',
        image: 'https://imgur.com/5Vvj3dD.jpg',
        location: "Uganda's National Parks",
        languages: ['English', 'Swahili', 'Rukiga'],
        specialties: ['Wildlife Safaris', 'Gorilla Trekking', 'Bird Watching', 'Photography Tours'],
        experience: 8,
        rating: 4.9,
        reviews: 156,
        bio: 'Professional wildlife guide with over 8 years of experience leading safaris across Uganda\'s national parks. Specialized in gorilla trekking and wildlife photography.',
        verified: true,
        joinedDate: '2015',
        completedTours: 450,
        responseTime: '< 1 hour',
        experiences: [
          {
            id: 1,
            title: 'Gorilla Trekking Adventure',
            image: 'https://imgur.com/LUalhrs.jpg',
            duration: '3 days',
            groupSize: '1-8 people',
            price: 1200,
            rating: 4.9,
            reviews: 127,
            location: 'Bwindi Impenetrable Forest',
            description: 'Experience the thrill of encountering mountain gorillas in their natural habitat.'
          },
          {
            id: 2,
            title: 'Wildlife Safari Experience',
            image: 'https://imgur.com/WqH4Bly.jpg',
            duration: '4 days',
            groupSize: '2-6 people',
            price: 1200,
            rating: 4.7,
            reviews: 78,
            location: 'Multiple Parks',
            description: 'Comprehensive wildlife safari across Uganda\'s top national parks.'
          },
          {
            id: 3,
            title: 'Bird Watching Tour',
            image: 'https://imgur.com/7qDwljW.jpg',
            duration: '2 days',
            groupSize: '1-4 people',
            price: 350,
            rating: 4.8,
            reviews: 45,
            location: 'Various Locations',
            description: 'Specialized bird watching tours for enthusiasts and photographers.'
          }
        ],
        certifications: [
          'Uganda Safari Guides Association',
          'First Aid Certified',
          'Wildlife Photography',
          'Conservation Leadership'
        ],
        achievements: [
          {
            title: 'Guide of the Year 2024',
            description: 'Awarded by Uganda Tourism Board'
          },
          {
            title: '450+ Tours Completed',
            description: 'With 98% satisfaction rate'
          },
          {
            title: 'Conservation Ambassador',
            description: 'Uganda Wildlife Authority'
          }
        ]
      },
      '2': {
        id: '2',
        name: 'Elisa Lule',
        image: 'https://imgur.com/ARZTtgG.jpg',
        location: 'Kampala',
        languages: ['English', 'Luganda'],
        specialties: ['Kampala City Tours', 'Cultural Experiences', 'Food Tours', 'Historical Sites'],
        experience: 5,
        rating: 4.8,
        reviews: 124,
        bio: 'Kampala native with extensive knowledge of the city\'s history, culture, and hidden gems. Specialized in creating authentic cultural experiences and food tours that showcase the real Kampala.',
        verified: true,
        joinedDate: '2018',
        completedTours: 280,
        responseTime: '< 2 hours',
        experiences: [
          {
            id: 1,
            title: 'Complete Kampala Experience',
            image: 'https://imgur.com/EzHnSsj.jpg',
            duration: 'Full Day',
            groupSize: '1-8 people',
            price: 25,
            rating: 4.9,
            reviews: 98,
            location: 'Kampala',
            description: 'Comprehensive tour of Kampala\'s highlights, from historical sites to modern developments.'
          },
          {
            id: 2,
            title: 'Kampala Food Tour',
            image: 'https://imgur.com/4kwMnNu.jpg',
            duration: '4 hours',
            groupSize: '2-8 people',
            price: 77,
            rating: 4.7,
            reviews: 76,
            location: 'Kampala',
            description: 'Taste the diverse flavors of Uganda through Kampala\'s best local eateries and markets.'
          },
          {
            id: 3,
            title: 'Cultural Heritage Tour',
            image: 'https://imgur.com/5v6lVl8.jpg',
            duration: 'Half Day',
            groupSize: '1-6 people',
            price: 45,
            rating: 4.8,
            reviews: 52,
            location: 'Kampala',
            description: 'Explore Kampala\'s rich cultural heritage through museums, cultural sites, and local communities.'
          }
        ],
        certifications: [
          'Kampala Tourism Association',
          'Cultural Heritage Guide',
          'Food Safety Certified',
          'First Aid Trained'
        ],
        achievements: [
          {
            title: 'Top City Guide 2023',
            description: 'Recognized by Kampala Tourism Board'
          },
          {
            title: '280+ Tours Completed',
            description: 'With 96% satisfaction rate'
          },
          {
            title: 'Cultural Ambassador',
            description: 'Uganda Museum'
          }
        ]
      },
      '3': {
        id: '3',
        name: 'Ankunda Prima',
        image: 'https://imgur.com/ugLHrmP.jpg',
        location: 'Multiple Locations',
        languages: ['English', 'Luganda', 'Runyankole'],
        specialties: ['Self-Drive Safaris', 'Adventure Tours', 'Customized Itineraries', 'Remote Destinations'],
        experience: 6,
        rating: 4.7,
        reviews: 98,
        bio: 'Self-drive safari specialist with extensive knowledge of Uganda\'s road networks and hidden gems. Expert in creating customized itineraries that take travelers off the beaten path to discover Uganda\'s lesser-known treasures.',
        verified: true,
        joinedDate: '2017',
        completedTours: 210,
        responseTime: '< 3 hours',
        experiences: [
          {
            id: 1,
            title: '12-Day Uganda\'s Finest',
            image: 'https://imgur.com/YmQ8sKD.jpg',
            duration: '12 days',
            groupSize: '2-6 people',
            price: 2400,
            rating: 4.9,
            reviews: 45,
            location: 'Multiple Parks',
            description: 'The ultimate self-drive adventure across Uganda\'s most spectacular landscapes and wildlife areas.'
          },
          {
            id: 2,
            title: '8-Day The Primate Express',
            image: 'https://imgur.com/PK2H77z.jpg',
            duration: '8 days',
            groupSize: '2-6 people',
            price: 2100,
            rating: 4.8,
            reviews: 38,
            location: 'Multiple Parks',
            description: 'Focus on Uganda\'s incredible primates including gorillas and chimpanzees with this self-drive itinerary.'
          },
          {
            id: 3,
            title: 'Off-Road Adventure',
            image: 'https://imgur.com/TvdP6Mt.jpg',
            duration: '5 days',
            groupSize: '2-4 people',
            price: 1800,
            rating: 4.7,
            reviews: 29,
            location: 'Remote Uganda',
            description: 'Explore Uganda\'s remote regions and off-the-beaten-path destinations on this adventurous self-drive tour.'
          }
        ],
        certifications: [
          'Advanced 4x4 Driving',
          'Wilderness First Responder',
          'Route Planning Specialist',
          'GPS Navigation Expert'
        ],
        achievements: [
          {
            title: 'Self-Drive Specialist',
            description: 'Recognized by Uganda Tourism Association'
          },
          {
            title: '210+ Tours Planned',
            description: 'With 95% satisfaction rate'
          },
          {
            title: 'Remote Destinations Expert',
            description: 'Featured in Travel Uganda Magazine'
          }
        ]
      },
      '4': {
        id: '4',
        name: 'Niwagaba Esser',
        image: 'https://imgur.com/lFI9D0f.jpg',
        location: 'Kibale Forest',
        languages: ['English', 'Swahili'],
        specialties: ['Chimpanzee Tracking', 'Kibale Forest Tours', 'Primate Experiences', 'Nature Walks'],
        experience: 4,
        rating: 4.8,
        reviews: 112,
        bio: 'Specialized in chimpanzee tracking and primate experiences in Kibale Forest National Park. Passionate about conservation and providing educational wildlife experiences that connect visitors with Uganda\'s incredible primate diversity.',
        verified: true,
        joinedDate: '2019',
        completedTours: 180,
        responseTime: '< 2 hours',
        experiences: [
          {
            id: 1,
            title: 'Chimpanzee Tracking Adventure',
            image: 'https://imgur.com/ePFOven.jpg',
            duration: '1 day',
            groupSize: '1-6 people',
            price: 200,
            rating: 4.9,
            reviews: 156,
            location: 'Kibale Forest',
            description: 'Track and observe chimpanzees in their natural habitat with expert guidance.'
          },
          {
            id: 2,
            title: 'Primate Discovery Tour',
            image: 'https://imgur.com/HE1ci9r.jpg',
            duration: '2 days',
            groupSize: '2-6 people',
            price: 350,
            rating: 4.8,
            reviews: 42,
            location: 'Kibale Forest',
            description: 'Comprehensive tour focusing on multiple primate species in Kibale Forest.'
          },
          {
            id: 3,
            title: 'Forest Nature Walk',
            image: 'https://imgur.com/8aMF2or.jpg',
            duration: '3 hours',
            groupSize: '1-8 people',
            price: 75,
            rating: 4.7,
            reviews: 68,
            location: 'Kibale Forest',
            description: 'Educational nature walk exploring the flora and fauna of Kibale Forest.'
          }
        ],
        certifications: [
          'Primate Specialist',
          'Kibale Forest Guide',
          'Wildlife Conservation',
          'First Aid Certified'
        ],
        achievements: [
          {
            title: 'Primate Conservation Award',
            description: 'Kibale Forest Conservation Project'
          },
          {
            title: '180+ Tours Completed',
            description: 'With 97% satisfaction rate'
          },
          {
            title: 'Research Assistant',
            description: 'Chimpanzee Habituation Project'
          }
        ]
      },
      '5': {
        id: '5',
        name: 'Farouk Ssendaire',
        image: 'https://imgur.com/5I0fk6J.jpg',
        location: 'Queen Elizabeth National Park',
        languages: ['English', 'Swahili', 'Runyankole'],
        specialties: ['Wildlife Safaris', 'Bird Watching', 'Photography Tours', 'Conservation Education'],
        experience: 5,
        rating: 4.8,
        reviews: 104,
        bio: 'Passionate safari guide with expertise in wildlife tracking and bird identification. Specializes in photography-focused safaris and conservation education in Queen Elizabeth National Park.',
        verified: true,
        joinedDate: '2020',
        completedTours: 185,
        responseTime: '< 2 hours',
        experiences: [
          {
            id: 1,
            title: 'Queen Elizabeth Safari',
            image: 'https://imgur.com/0MEx54w.jpg',
            duration: '3 days',
            groupSize: '2-6 people',
            price: 450,
            rating: 4.9,
            reviews: 48,
            location: 'Queen Elizabeth National Park',
            description: 'Comprehensive safari experience in Uganda\'s most popular national park, featuring tree-climbing lions and diverse wildlife.'
          },
          {
            id: 2,
            title: 'Bird Watching Expedition',
            image: 'https://imgur.com/7qDwljW.jpg',
            duration: '2 days',
            groupSize: '1-4 people',
            price: 300,
            rating: 4.8,
            reviews: 32,
            location: 'Queen Elizabeth National Park',
            description: 'Specialized bird watching tour focusing on the park\'s 600+ bird species.'
          },
          {
            id: 3,
            title: 'Photography Safari',
            image: 'https://imgur.com/Rl4EEiV.jpg',
            duration: '4 days',
            groupSize: '1-3 people',
            price: 600,
            rating: 4.9,
            reviews: 24,
            location: 'Queen Elizabeth National Park',
            description: 'Photography-focused safari with expert guidance on capturing wildlife in their natural habitat.'
          }
        ],
        certifications: [
          'Wildlife Guide Certification',
          'Bird Identification Specialist',
          'Conservation Education',
          'First Aid Certified'
        ],
        achievements: [
          {
            title: 'Top Wildlife Guide 2023',
            description: 'Queen Elizabeth National Park'
          },
          {
            title: '185+ Tours Completed',
            description: 'With 98% satisfaction rate'
          },
          {
            title: 'Conservation Ambassador',
            description: 'Uganda Wildlife Authority'
          }
        ]
      },
      '7': {
        id: '7',
        name: 'Enoch',
        image: 'https://imgur.com/yDiQEtu.jpg',
        location: 'Kidepo Valley National Park',
        languages: ['English', 'Swahili', 'Karamojong'],
        specialties: ['Remote Safaris', 'Photography', 'Cultural Experiences', 'Wildlife Tracking'],
        experience: 7,
        rating: 4.9,
        reviews: 42,
        bio: 'Remote safari specialist with extensive knowledge of Kidepo Valley National Park. Combines wildlife expertise with cultural insights from the Karamojong people for a truly immersive experience.',
        verified: true,
        joinedDate: '2018',
        completedTours: 165,
        responseTime: '< 3 hours',
        experiences: [
          {
            id: 1,
            title: 'Kidepo Wilderness Safari',
            image: 'https://imgur.com/jLwqpmS.jpg',
            duration: '6 days',
            groupSize: '2-6 people',
            price: 1800,
            rating: 4.9,
            reviews: 32,
            location: 'Kidepo Valley',
            description: 'Explore one of Africa\'s most remote and pristine wildlife areas with unique species not found elsewhere in Uganda.'
          },
          {
            id: 2,
            title: 'Karamojong Cultural Experience',
            image: 'https://imgur.com/5v6lVl8.jpg',
            duration: '3 days',
            groupSize: '2-8 people',
            price: 450,
            rating: 4.8,
            reviews: 28,
            location: 'Karamoja Region',
            description: 'Immersive cultural experience with the Karamojong people, including village visits and traditional activities.'
          },
          {
            id: 3,
            title: 'Photography Expedition',
            image: 'https://imgur.com/Rl4EEiV.jpg',
            duration: '5 days',
            groupSize: '1-4 people',
            price: 1200,
            rating: 4.9,
            reviews: 18,
            location: 'Kidepo Valley',
            description: 'Specialized photography safari focusing on Kidepo\'s unique landscapes and wildlife.'
          }
        ],
        certifications: [
          'Advanced Wildlife Tracking',
          'Cultural Heritage Guide',
          'Wilderness First Responder',
          'Photography Instructor'
        ],
        achievements: [
          {
            title: 'Kidepo Specialist Guide',
            description: 'Recognized by Uganda Tourism Board'
          },
          {
            title: '165+ Tours Completed',
            description: 'With 99% satisfaction rate'
          },
          {
            title: 'Cultural Ambassador',
            description: 'Karamoja Tourism Initiative'
          }
        ]
      },
      '9': {
        id: '9',
        name: 'Azel John Makumbi',
        image: 'https://imgur.com/YTHr1B6.jpg',
        location: 'Kampala',
        languages: ['English', 'Luganda'],
        specialties: ['Kampala City Tours', 'Kampala Night Life', 'Underground Scene', 'Cultural Experiences'],
        experience: 4,
        rating: 4.8,
        reviews: 87,
        bio: 'Specialized in Kampala city tours and nightlife experiences, showing visitors the vibrant urban culture of Uganda\'s capital. Azel has insider knowledge of the underground scene and exclusive venues that most tourists never discover.',
        verified: true,
        joinedDate: '2021',
        completedTours: 145,
        responseTime: '< 1 hour',
        experiences: [
          {
            id: 1,
            title: 'Kampala Night Crawling',
            image: 'https://images.unsplash.com/photo-1566737236500-c8ac43014a67?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
            duration: '6 hours',
            groupSize: '2-8 people',
            price: 20,
            rating: 4.9,
            reviews: 42,
            location: 'Kampala',
            description: 'Experience Kampala\'s vibrant nightlife with VIP access to the hottest clubs, delicious street food, and expert local guides.'
          },
          {
            id: 2,
            title: 'Complete Kampala Experience',
            image: 'https://imgur.com/EzHnSsj.jpg',
            duration: 'Full Day',
            groupSize: '1-8 people',
            price: 25,
            rating: 4.8,
            reviews: 35,
            location: 'Kampala',
            description: 'Comprehensive tour of Kampala\'s highlights, from historical sites to modern developments.'
          },
          {
            id: 3,
            title: 'Premium VIP Nightlife Experience',
            image: 'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80',
            duration: '8 hours',
            groupSize: '2-6 people',
            price: 50,
            rating: 4.9,
            reviews: 28,
            location: 'Kampala',
            description: 'Our premium package includes everything you need for an unforgettable night: bottle service, private transportation, a professional photographer, and access to exclusive VIP areas.'
          }
        ],
        certifications: [
          'Kampala Tourism Association',
          'Nightlife Safety Certified',
          'Cultural Heritage Guide',
          'First Aid Trained'
        ],
        achievements: [
          {
            title: 'Nightlife Expert',
            description: 'Recognized by Kampala Entertainment Association'
          },
          {
            title: '145+ Tours Completed',
            description: 'With 98% satisfaction rate'
          },
          {
            title: 'Featured Guide',
            description: 'Kampala Nightlife Magazine 2024'
          }
        ]
      },
      '10': {
        id: '10',
        name: 'Emmy Frass',
        image: 'https://imgur.com/tLBau6N.jpg',
        location: 'Kampala',
        languages: ['English', 'Luganda', 'Swahili'],
        specialties: ['Kampala Night Crawling', 'Nightlife Guide', 'Club VIP Access', 'Entertainment Districts'],
        experience: 5,
        rating: 4.9,
        reviews: 94,
        bio: 'Expert nightlife guide specializing in Kampala night crawling experiences, club tours, and the best entertainment spots in the city. Emmy knows all the hottest venues and can get you VIP access to the most exclusive clubs in Kampala.',
        verified: true,
        joinedDate: '2020',
        completedTours: 210,
        responseTime: '< 30 minutes',
        experiences: [
          {
            id: 1,
            title: 'Kampala Night Crawling',
            image: 'https://images.unsplash.com/photo-1566737236500-c8ac43014a67?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
            duration: '6 hours',
            groupSize: '2-8 people',
            price: 20,
            rating: 4.9,
            reviews: 56,
            location: 'Kampala',
            description: 'Experience Kampala\'s vibrant nightlife with VIP access to the hottest clubs, delicious street food, and expert local guides.'
          },
          {
            id: 2,
            title: 'Premium VIP Experience',
            image: 'https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1829&q=80',
            duration: '8 hours',
            groupSize: '2-6 people',
            price: 50,
            rating: 4.9,
            reviews: 38,
            location: 'Kampala',
            description: 'Our premium package includes everything you need for an unforgettable night: bottle service, private transportation, a professional photographer, and access to exclusive VIP areas.'
          },
          {
            id: 3,
            title: 'Underground Club Tour',
            image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80',
            duration: '5 hours',
            groupSize: '2-6 people',
            price: 35,
            rating: 4.8,
            reviews: 24,
            location: 'Kampala',
            description: 'Discover Kampala\'s hidden underground club scene with access to exclusive venues that most tourists never find.'
          }
        ],
        certifications: [
          'Nightlife Safety Certified',
          'VIP Club Relations',
          'Event Management',
          'First Aid Trained'
        ],
        achievements: [
          {
            title: 'Top Nightlife Guide 2024',
            description: 'Kampala Entertainment Awards'
          },
          {
            title: '210+ Tours Completed',
            description: 'With 99% satisfaction rate'
          },
          {
            title: 'VIP Relations Manager',
            description: 'For multiple premium Kampala clubs'
          }
        ]
      }
    };

    const guideData = mockGuides[id || '1'];
    if (guideData) {
      setGuide(guideData);
      // Set document title
      document.title = `${guideData.name} - TravelCord Guide`;
    }
    
    window.scrollTo(0, 0);
  }, [id]);

  if (!guide) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ta-green"></div>
      </div>
    );
  }

  // Determine if this is a nightlife guide
  const isNightlifeGuide = guide.id === '9' || guide.id === '10';

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src={isNightlifeGuide ? 
              "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2670&q=80" : 
              "https://imgur.com/WqH4Bly.jpg"}
            alt="Safari background"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/travel-guides" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Guides
            </Link>
          </div>
          
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="relative">
              <img 
                src={guide.image}
                alt={guide.name}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white"
              />
              {guide.verified && (
                <div className="absolute -bottom-2 -right-2 bg-ta-green text-white text-xs px-2 py-0.5 rounded-full flex items-center">
                  <Shield className="h-3 w-3 mr-1" />
                  Verified
                </div>
              )}
            </div>
            
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-3xl md:text-4xl font-bold">{guide.name}</h1>
                <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-2 py-1">
                  <Star className="h-4 w-4 fill-current text-yellow-400 mr-1" />
                  <span>{guide.rating}</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/90">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 text-ta-green mr-1" />
                  {guide.location}
                </div>
                <div className="flex items-center">
                  <Languages className="h-4 w-4 text-ta-green mr-1" />
                  {guide.languages.join(', ')}
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 text-ta-green mr-1" />
                  {guide.experience} years experience
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{guide.completedTours}+</div>
              <div className="text-sm text-gray-600">Tours Completed</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{guide.experience} Years</div>
              <div className="text-sm text-gray-600">Experience</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{guide.languages.length}</div>
              <div className="text-sm text-gray-600">Languages</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{guide.responseTime}</div>
              <div className="text-sm text-gray-600">Response Time</div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <div className="sticky top-14 z-20 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('experiences')}
              className={`py-4 text-sm font-medium border-b-2 ${
                activeTab === 'experiences'
                  ? isNightlifeGuide ? 'border-purple-600 text-purple-600' : 'border-ta-green text-ta-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Experiences
            </button>
            <button
              onClick={() => setActiveTab('about')}
              className={`py-4 text-sm font-medium border-b-2 ${
                activeTab === 'about'
                  ? isNightlifeGuide ? 'border-purple-600 text-purple-600' : 'border-ta-green text-ta-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              About
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'experiences' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guide.experiences.map((experience) => (
                <div 
                  key={experience.id}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  <div className="relative h-48">
                    <img 
                      src={experience.image}
                      alt={experience.title}
                      className="w-full h-full object-cover"
                    />
                    <button className="absolute top-2 right-2 p-1.5 bg-white/80 rounded-full hover:bg-white transition-colors">
                      <Heart className="h-4 w-4 text-gray-600 hover:text-red-500" />
                    </button>
                  </div>

                  <div className="p-4">
                    <div className="flex items-center mb-2">
                      <Star className="h-4 w-4 text-yellow-400 fill-current" />
                      <span className="ml-1 text-sm font-medium">{experience.rating}</span>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{experience.title}</h3>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="h-4 w-4 text-ta-green mr-1" />
                        {experience.location}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 text-ta-green mr-1" />
                        {experience.duration}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="h-4 w-4 text-ta-green mr-1" />
                        {experience.groupSize}
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">{experience.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">from</p>
                        <p className="text-xl font-bold text-gray-900">${experience.price}</p>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedExperience(experience);
                          setIsBookingModalOpen(true);
                        }}
                        className={`px-4 py-2 text-white rounded-lg text-sm transition-colors ${
                          isNightlifeGuide ? 'bg-purple-600 hover:bg-purple-700' : 'bg-ta-green hover:bg-ta-green-dark'
                        }`}
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'about' && (
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">About {guide.name}</h2>
                <p className="text-gray-600 mb-6">{guide.bio}</p>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">Specialties</h3>
                <div className="flex flex-wrap gap-2 mb-6">
                  {guide.specialties.map((specialty, index) => (
                    <span 
                      key={index}
                      className={`px-3 py-1 rounded-full text-sm ${
                        isNightlifeGuide ? 
                        'bg-purple-100 text-purple-800' : 
                        'bg-ta-green/10 text-ta-green'
                      }`}
                    >
                      {specialty}
                    </span>
                  ))}
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">Certifications</h3>
                <div className="space-y-2 mb-6">
                  {guide.certifications.map((cert, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className={`h-5 w-5 mr-2 ${isNightlifeGuide ? 'text-purple-600' : 'text-ta-green'}`} />
                      <span className="text-gray-600">{cert}</span>
                    </div>
                  ))}
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">Achievements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {guide.achievements.map((achievement, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-1">{achievement.title}</h4>
                      <p className="text-sm text-gray-600">{achievement.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Booking Modal */}
      {selectedExperience && (
        <BookingModal
          experience={selectedExperience}
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedExperience(null);
          }}
        />
      )}

      <Footer />
    </>
  );
};

export default GuideProfile;